import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useSSRSafeId as $b5e257d569688ac6$export$619500959fc48b26 } from '../../node_modules/@react-aria/ssr/dist/import.js';
import classNames from 'classnames';
import React__default, { isValidElement, cloneElement } from 'react';
import { FormHelperText } from '../Form/FormHelperText.js';
import { FormLabel } from '../Form/FormLabel.js';
import { applyCommonProps } from '../utility/commonProps.js';
import styles from './TextField.module.scss.js';

var _excluded = ["className", "disabled", "error", "helperText", "id", "label", "leadingIcon", "onChange", "placeholder", "readOnly", "size", "textFieldProps", "trailing", "type", "value", "rounded"];
var autoComplete = {
  email: 'email',
  password: 'new-password',
  tel: 'tel'
};
/**
 * Text Fields allow users to enter and edit text.
 *
 * {@link https://livingdesign.walmart.com/components/text-field/ Guidelines}
 * {@link https://livingdesign.walmart.com/components/text-field/react/ React documentation}
 *
 * ### Content
 *
 * #### Label
 *
 * Text Field requires a label to be accessible. Use the `label` property to add a label:
 *
 * ```tsx
 * <TextField
 *   label="Full name"
 *   onChange={(event) => console.log(event)}
 * />
 * ```
 *
 * #### Helper and error text
 *
 * Set the `helperText` prop to include descriptive helper text below the label:
 *
 * ```tsx
 * <TextField
 *   helperText="Please include middle initials"
 *   label="Full name"
 *   onChange={(event) => console.log(event)}
 * />
 * ```
 *
 * Set the `error` prop to indicate to a user that the Text Field is in a bad state.
 *
 * (Note: `error` overrides `helperText`.)
 *
 * ```tsx
 * <TextField
 *   error="This is wrong."
 *   label="Full name"
 *   onChange={(event) => console.log(event)}
 * />
 * ```
 *
 * #### Leading and trailing
 *
 * Clarify the Text Field's purpose by adding an icon with the `leadingIcon` prop:
 *
 * ```tsx
 * import * as React from "react";
 * import * as Icon from "@livingdesign/icons";
 * import { TextField } from "@walmart-web/livingdesign-components";
 *
 * export default () => {
 *   return (
 *     <TextField
 *       label="Full name"
 *       leadingIcon={<Icon.User />}
 *       onChange={(event) => console.log(event)}
 *     />
 *   );
 * };
 * ```
 *
 * Enhance Text Field's functionality by passing additional content to the `trailing` slot:
 *
 * ```tsx
 * import * as React from "react";
 * import * as Icon from "@livingdesign/icons";
 * import { IconButton, TextField } from "@walmart-web/livingdesign-components";
 *
 * export default () => {
 *   return (
 *     <TextField
 *       label="Full name"
 *       onChange={(event) => console.log(event)}
 *       trailing={
 *         <IconButton a11yLabel="Clear full name">
 *           <Icon.CloseCircleFill />
 *         </IconButton>
 *       }
 *     />
 *   );
 * };
 * ```
 *
 * ### Features
 *
 * #### Size
 *
 * The Text Area comes in two sizes:
 *
 * - `small`
 * - `large` (default)
 *
 * Set the Text Field's size with the `size` property.
 *
 * ```tsx
 * <TextField label="Small" size="small" />
 * <TextField label="Large" size="large" />
 * ```
 *
 * #### Rounded Borders
 *
 * The Text Field comes with rounded borders. Set the value of rounded prop to true to get the rounded borders.
 *
 * ```tsx
 * <TextField label="Rounded Borders" rounded={true} />
 * <Textfield label="Normal Borders" rounded={false} /> (default)
 * ````
 *
 * ### Interaction
 *
 * Configure the Text Field's value using the `value` property. Respond to changes by passing an callback handler to the `onChange` prop:
 *
 * ```tsx
 * import * as React from "react";
 * import { TextField } from "@walmart-web/livingdesign-components";
 *
 * export default () => {
 *   const [value, setValue] = React.useState("CA");
 *
 *   return (
 *     <TextField
 *       label="Full name"
 *       onChange={(event) => setValue(event.target.value)}
 *       value={value}
 *     />
 *   );
 * };
 * ```
 */
var TextField = /*#__PURE__*/React__default.forwardRef(function (props, ref) {
  var _applyCommonProps = applyCommonProps(props, TextField),
    className = _applyCommonProps.className,
    _applyCommonProps$dis = _applyCommonProps.disabled,
    disabled = _applyCommonProps$dis === void 0 ? false : _applyCommonProps$dis,
    error = _applyCommonProps.error,
    helperText = _applyCommonProps.helperText,
    initialId = _applyCommonProps.id,
    label = _applyCommonProps.label,
    leadingIcon = _applyCommonProps.leadingIcon,
    onChange = _applyCommonProps.onChange,
    placeholder = _applyCommonProps.placeholder,
    _applyCommonProps$rea = _applyCommonProps.readOnly,
    readOnly = _applyCommonProps$rea === void 0 ? false : _applyCommonProps$rea,
    _applyCommonProps$siz = _applyCommonProps.size,
    size = _applyCommonProps$siz === void 0 ? 'medium' : _applyCommonProps$siz,
    textFieldProps = _applyCommonProps.textFieldProps,
    trailing = _applyCommonProps.trailing,
    _applyCommonProps$typ = _applyCommonProps.type,
    type = _applyCommonProps$typ === void 0 ? 'text' : _applyCommonProps$typ,
    _applyCommonProps$val = _applyCommonProps.value,
    value = _applyCommonProps$val === void 0 ? '' : _applyCommonProps$val,
    _applyCommonProps$rou = _applyCommonProps.rounded,
    rounded = _applyCommonProps$rou === void 0 ? false : _applyCommonProps$rou,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  var id = $b5e257d569688ac6$export$619500959fc48b26(initialId);
  var helperId = $b5e257d569688ac6$export$619500959fc48b26();
  var helperContent = error || helperText;
  var getAdjustedSize = function getAdjustedSize(inputSize) {
    if (inputSize === 'large') {
      console.warn('"large" size is deprecated and will be removed in future version. Use "medium" instead.');
      return 'medium';
    }
    return inputSize;
  };
  var adjustedSize = getAdjustedSize(size);
  return /*#__PURE__*/React__default.createElement("div", _extends({
    className: classNames(styles.root, disabled && styles.disabled, error && styles.error, leadingIcon && styles.hasLeadingIcon, adjustedSize === 'medium' && styles.medium, adjustedSize === 'small' && styles.small, className)
  }, rest), /*#__PURE__*/React__default.createElement(FormLabel, {
    className: styles.label,
    disabled: disabled,
    htmlFor: id,
    size: "large"
  }, label), /*#__PURE__*/React__default.createElement("div", {
    className: classNames(styles.container, rounded && styles.rounded, disabled && styles.containerDisabled)
  }, leadingIcon && /*#__PURE__*/React__default.createElement("span", {
    "aria-hidden": true,
    className: classNames(styles.icon, styles.leadingIcon)
  }, /*#__PURE__*/isValidElement(leadingIcon) ? /*#__PURE__*/cloneElement(leadingIcon, {
    size: 'small'
  }) : leadingIcon), /*#__PURE__*/React__default.createElement("input", _extends({
    "aria-describedby": helperContent ? helperId : undefined,
    autoComplete: type && autoComplete[type],
    disabled: disabled,
    id: id,
    onChange: onChange,
    placeholder: placeholder,
    readOnly: readOnly,
    ref: ref,
    type: type,
    value: value
  }, textFieldProps, {
    className: classNames(styles.value, textFieldProps === null || textFieldProps === void 0 ? void 0 : textFieldProps.className)
  })), trailing && /*#__PURE__*/React__default.createElement("span", {
    "aria-hidden": true,
    className: classNames(styles.icon, styles.trailing, readOnly && styles.readOnly)
  }, /*#__PURE__*/isValidElement(trailing) ? /*#__PURE__*/cloneElement(trailing, {
    size: 'small'
  }) : trailing)), !!helperContent && /*#__PURE__*/React__default.createElement(FormHelperText, {
    className: styles.helperText,
    disabled: disabled,
    id: helperId,
    hasError: !!error
  }, helperContent));
});
TextField.displayName = 'TextField';

export { TextField };
